import { upperFirst } from 'lodash-es'

import { FvLinkButton, Icon } from '@fv/client-components'
import {
  formatNumber,
  getDistanceDisplay,
  getEquipmentLabel,
} from '@fv/client-core'
import type { PropsWithLoad } from '@fv/client-types'
import type { EquipmentType } from '@fv/models/equipment'

import { HasPermission } from '@/features/auth'
import { ComparableLoadsInline } from '@/features/comparable-loads/ComparableLoadsInline'
import { useCurrency, useLocale, useTypeNames } from '@/hooks/settings'
import { useLoadEditRoute } from '@/route-hooks'
import { routes } from '@/routes'

import { ListItem } from '../List'

type SummaryProps = PropsWithLoad<{
  equipmentType?: EquipmentType
  isBooking?: boolean
  showItems?: () => void
}>

export const TruckloadSummary = ({
  equipmentType,
  isBooking,
  load,
  showItems,
}: SummaryProps) => {
  const { accessorialName, equipmentName } = useTypeNames()
  const locale = useLocale()
  const priceParts = useCurrency()

  const { distance, equipment, items, workflow } = load
  if (workflow !== 'truckload') return null

  const {
    accessorials = [],
    alternateTypes,
    declaredValue,
    description,
    isHazardous,
    type,
    weight,
    weightUOM,
  } = equipment

  const [dollars, cents] = priceParts(declaredValue)
  const equipmentLabel = getEquipmentLabel({
    equipmentName: equipmentName(equipmentType ?? type),
    ...(!equipmentType && {
      altEquipmentNames: alternateTypes?.map(equipmentName),
    }),
  })

  return (
    <>
      <ListItem className="b1200:first:pl-0">
        <p className="mb-0 flex flex-nowrap items-start">
          <Icon icon="truck" className="text-fv-gray-dark" transform="down-3" />
          <span>{equipmentLabel}</span>
        </p>
      </ListItem>
      {!!distance && (
        <ListItem>
          <Icon icon="route" className="text-fv-gray-dark" />
          <span>{getDistanceDisplay(load, locale)}</span>
        </ListItem>
      )}
      <ListItem itemsStart>
        {!isBooking && !!items?.length ? (
          <FvLinkButton icon="list" onClick={showItems}>
            <span>{upperFirst(description)}</span>
          </FvLinkButton>
        ) : (
          <>
            <Icon
              icon="quote-left"
              transform="down-3.75"
              className="text-fv-gray-dark"
            />
            <span>{upperFirst(description)}</span>
          </>
        )}
      </ListItem>

      <ListItem>
        <Icon icon="weight-hanging" className="text-fv-gray-dark" />
        <span>
          {formatNumber(weight, locale)} {weightUOM}.
        </span>
      </ListItem>

      {declaredValue && (
        <ListItem>
          <Icon icon="dollar-sign" className="text-fv-gray-dark" />
          <span>
            {dollars}.{cents}
          </span>
        </ListItem>
      )}

      {isHazardous && (
        <ListItem>
          <Icon icon="radiation-alt" className="text-fv-orange" />
          <span>Hazardous</span>
        </ListItem>
      )}

      {accessorials.length > 0 && (
        <ListItem itemsStart className="flex-nowrap">
          <Icon icon="list" className="text-fv-gray-dark flex-none" />
          <span>
            Accessorials:{' '}
            {accessorials.map(a => accessorialName(a.key)).join(', ')}
          </span>
        </ListItem>
      )}

      <ComparableLoadsInline load={load} layout="list-item" />
    </>
  )
}

type ActionProps = PropsWithLoad<{
  cancelRequest: (all: boolean, redirect: string) => void
  hasAwardedEquipment?: boolean
  isBooking?: boolean
  isCanceling: boolean
  isEditingLoad?: boolean
  openCancellationPanel: () => void
}>

export const TruckloadActions = ({
  cancelRequest,
  hasAwardedEquipment,
  isBooking,
  isCanceling,
  isEditingLoad,
  load,
  openCancellationPanel,
}: ActionProps) => {
  const { status, workflow } = load
  const editRoute = useLoadEditRoute()
  if (workflow !== 'truckload') return null

  const canEditRequest =
    (status === 'pending' || load.routeGuide?.status === 'running') &&
    !load.isFreightCollect
  const canStartOver = !hasAwardedEquipment && !isBooking

  function startOver() {
    const confirmed = window.confirm(
      'Starting over will cancel this quote request. Are you sure?',
    )

    if (!confirmed) return
    cancelRequest(true, editRoute(load, 'copy'))
  }

  return (
    <>
      {canStartOver && (
        <HasPermission name="load-booker">
          <ListItem>
            <FvLinkButton
              disabled={isCanceling}
              icon={isCanceling ? 'spinner' : 'edit'}
              onClick={startOver}
            >
              Start over
            </FvLinkButton>
          </ListItem>
        </HasPermission>
      )}

      {canEditRequest && (
        <HasPermission name="load-booker">
          <ListItem>
            <FvLinkButton
              disabled={isCanceling}
              icon="pen-alt"
              to={editRoute(load, 'edit')}
            >
              Edit request
            </FvLinkButton>
          </ListItem>
        </HasPermission>
      )}

      {!isBooking && (
        <HasPermission name="load-canceler">
          <ListItem>
            <FvLinkButton
              disabled={isCanceling}
              icon={isCanceling ? 'spinner' : 'trash'}
              onClick={openCancellationPanel}
            >
              Cancel this quote request
            </FvLinkButton>
          </ListItem>
        </HasPermission>
      )}

      {isBooking && !isEditingLoad && !load.isFreightCollect && (
        <ListItem>
          <FvLinkButton
            disabled={isCanceling}
            icon="arrow-to-left"
            to={routes.rates(load)}
          >
            Back to quote request
          </FvLinkButton>
        </ListItem>
      )}
    </>
  )
}
